import { useEffect } from 'react'
import { useDocumentState } from '@/state'
import { useShallow } from 'zustand/react/shallow'
import { useBulkDeleteTemplateAttributes } from '@/services/queries/template_attributes'

const isEditableKeyboardTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false

  return (
    Boolean(target.closest('.ProseMirror')) ||
    Boolean(target.isContentEditable) ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    (target.tagName === 'DIV' && target.getAttribute('role') === 'textbox')
  )
}

export const useEnabledTemplateAttributesMultiSelect = () => {
  const { mutateAsync: bulkDeleteTemplateAttributes, isPending: isDeleting } =
    useBulkDeleteTemplateAttributes()

  const getDocumentState = useDocumentState(
    useShallow((state) => state.getState),
  )
  const setDocumentState = useDocumentState(
    useShallow((state) => state.setState),
  )

  useEffect(() => {
    const handleDelete = async (e: KeyboardEvent) => {
      if (
        e.defaultPrevented ||
        isDeleting ||
        (e.key !== 'Delete' && e.key !== 'Backspace') ||
        isEditableKeyboardTarget(e.target)
      ) {
        return
      }

      const { currentPageInView, bulkSelectedTemplateAttributes } =
        getDocumentState()

      if (currentPageInView && bulkSelectedTemplateAttributes.length > 0) {
        e.preventDefault()
        await bulkDeleteTemplateAttributes({
          documentPageId: currentPageInView,
          templateAttributes: bulkSelectedTemplateAttributes,
        })
        setDocumentState({
          bulkSelectedTemplateAttributes: [],
          selectedElement: null,
        })
      }
    }

    document.addEventListener('keydown', handleDelete)

    return () => {
      document.removeEventListener('keydown', handleDelete)
    }
  }, [
    bulkDeleteTemplateAttributes,
    getDocumentState,
    isDeleting,
    setDocumentState,
  ])
}
